import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminSection from '../../components/admin/ui/AdminSection';
import AdminButton from '../../components/admin/ui/AdminButton';
import AdminStatusBadge from '../../components/admin/ui/AdminStatusBadge';
import AdminAlert from '../../components/admin/ui/AdminAlert';
import AdminTable, {
  AdminTableHeader,
  AdminTableRow,
  AdminTableCell,
  AdminTableHeaderCell,
} from '../../components/admin/ui/AdminTable';
import { apiGet, apiPost } from '../../services/apiClient.js';

const POLL_MS = 5000;
const HISTORY_LIMIT = 20;
const DESC_MIN = 10;

function formatRelativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (Number.isNaN(diff)) return '';
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

function statusToBadgeVariant(st) {
  switch (st) {
    case 'success':
      return 'success';
    case 'failed':
      return 'error';
    case 'in_progress':
      return 'warning';
    case 'triggered':
      return 'info';
    default:
      return 'neutral';
  }
}

function formatStatusLabel(st) {
  if (!st) return 'unknown';
  return String(st).replace(/_/g, ' ');
}

function parseTriggeredByDisplay(raw) {
  if (raw == null || raw === '') return '—';
  if (typeof raw === 'object') {
    return raw.email || raw.id || '—';
  }
  try {
    const o = JSON.parse(raw);
    return o.email || o.id || '—';
  } catch {
    return String(raw).length > 64 ? `${String(raw).slice(0, 64)}…` : String(raw);
  }
}

function truncateSha(sha) {
  if (!sha) return 'unknown';
  return sha.length > 8 ? sha.slice(0, 8) : sha;
}

function deployTerminal(status) {
  return status === 'success' || status === 'failed';
}

async function fetchDeployStatus() {
  const data = await apiGet('/admin/deploy/status');
  if (data?.status === 'no_deploys') return { kind: 'empty' };
  return { kind: 'row', row: data };
}

const modalStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 'var(--z-modal)',
  },
  modal: {
    backgroundColor: 'var(--color-bg-primary)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--elevation-3)',
    width: '90%',
    maxWidth: '520px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-4) var(--spacing-5)',
    borderBottom: '1px solid var(--color-border-light)',
  },
  title: {
    margin: 0,
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: 'var(--font-size-xl)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    padding: 'var(--spacing-1)',
    lineHeight: 1,
    borderRadius: 'var(--radius-sm)',
  },
  content: {
    padding: 'var(--spacing-5)',
    flex: 1,
    overflowY: 'auto',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-4) var(--spacing-5)',
    borderTop: '1px solid var(--color-border-light)',
  },
  label: {
    display: 'block',
    marginBottom: 'var(--spacing-2)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-sm)',
  },
  textarea: {
    width: '100%',
    padding: 'var(--spacing-3) var(--spacing-4)',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-family)',
    resize: 'vertical',
    minHeight: '120px',
    backgroundColor: 'var(--color-bg-primary)',
    color: 'var(--color-text-primary)',
  },
  help: {
    marginTop: 'var(--spacing-2)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
  },
  errorBox: {
    marginTop: 'var(--spacing-3)',
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-status-error-bg)',
    color: 'var(--color-status-error-text)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
  },
};

export default function DeployPage() {
  const [statusBundle, setStatusBundle] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const [history, setHistory] = useState({ deploys: [], total: 0 });
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [expandedId, setExpandedId] = useState(null);
  const [logById, setLogById] = useState({});
  const [logLoadingId, setLogLoadingId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerError, setTriggerError] = useState(null);
  const [bannerSuccess, setBannerSuccess] = useState(null);

  const [prodConfirmOpen, setProdConfirmOpen] = useState(false);
  const [prodConfirmKind, setProdConfirmKind] = useState(null);
  const [prodActionLoading, setProdActionLoading] = useState(false);
  const [prodOutput, setProdOutput] = useState('');
  const [prodResultKind, setProdResultKind] = useState(null);
  const [prodPolling, setProdPolling] = useState(false);
  const [prodLineMessage, setProdLineMessage] = useState('');
  const [prodDeployStatus, setProdDeployStatus] = useState(null);

  const pollRef = useRef(null);
  const prodPollRef = useRef(null);

  const latest =
    statusBundle?.kind === 'row' ? statusBundle.row : null;
  const noDeploys = statusBundle?.kind === 'empty';

  const loadStatus = useCallback(async () => {
    try {
      const bundle = await fetchDeployStatus();
      setStatusBundle(bundle);
    } catch {
      setStatusBundle(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiGet(
        `/admin/deploy/history?limit=${HISTORY_LIMIT}&offset=${historyOffset}`
      );
      setHistory({ deploys: res.deploys || [], total: res.total ?? 0 });
    } catch {
      setHistory({ deploys: [], total: 0 });
    } finally {
      setHistoryLoading(false);
    }
  }, [historyOffset]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const shouldPoll = latest && !deployTerminal(latest.status);

  useEffect(() => {
    if (!shouldPoll) {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(() => {
      loadStatus();
    }, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [shouldPoll, loadStatus]);

  const stopProdPolling = useCallback(() => {
    if (prodPollRef.current != null) {
      clearInterval(prodPollRef.current);
      prodPollRef.current = null;
    }
    setProdPolling(false);
  }, []);

  const pollProdDeployStatusOnce = useCallback(async () => {
    try {
      const data = await apiGet('/admin/deploy/prod/status');
      const ds = data.deployStatus ?? null;
      setProdDeployStatus(ds);
      const text = data.output ?? `${data.stdout ?? ''}${data.stderr ? `\n${data.stderr}` : ''}`;
      setProdOutput(text || JSON.stringify(data, null, 2));

      if (ds?.status === 'success') {
        stopProdPolling();
        setProdResultKind('success');
        const sha = ds.git_sha || ds.sha || ds.gitSha;
        const done = ds.completed_at || ds.completedAt;
        setProdLineMessage(
          `Production deploy succeeded.${sha ? ` SHA: ${sha}` : ''}${done ? ` · Completed: ${done}` : ''}`
        );
      } else if (ds?.status === 'failed') {
        stopProdPolling();
        setProdResultKind('error');
        setProdLineMessage(ds.error_message || ds.error || ds.message || 'Production deploy failed');
      } else if (ds?.status === 'in_progress') {
        const t = ds.started_at || ds.startedAt || ds.start_time || ds.startTime;
        setProdLineMessage(t ? `Deploy in progress… (started: ${t})` : 'Deploy in progress…');
        setProdResultKind(null);
      } else {
        setProdLineMessage('Waiting for deploy status from production…');
        setProdResultKind(null);
      }
    } catch (err) {
      stopProdPolling();
      setProdResultKind('error');
      setProdLineMessage(err.message || 'Status poll failed');
      setProdOutput(err.message || 'Request failed');
    }
  }, [stopProdPolling]);

  const startProdDeployPolling = useCallback(() => {
    stopProdPolling();
    setProdPolling(true);
    setProdDeployStatus(null);
    void pollProdDeployStatusOnce();
    prodPollRef.current = setInterval(() => {
      void pollProdDeployStatusOnce();
    }, POLL_MS);
  }, [stopProdPolling, pollProdDeployStatusOnce]);

  const refreshProdDeployStatus = useCallback(() => {
    void pollProdDeployStatusOnce();
  }, [pollProdDeployStatusOnce]);

  useEffect(
    () => () => {
      if (prodPollRef.current != null) {
        clearInterval(prodPollRef.current);
        prodPollRef.current = null;
      }
    },
    []
  );

  const openModal = () => {
    setDescription('');
    setTriggerError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (triggerLoading) return;
    setModalOpen(false);
  };

  const confirmDeploy = async () => {
    const d = description.trim();
    if (d.length < DESC_MIN) return;
    setTriggerLoading(true);
    setTriggerError(null);
    try {
      await apiPost('/admin/deploy/trigger', { description: d });
      setBannerSuccess('Deploy started. Status will update automatically.');
      setModalOpen(false);
      setDescription('');
      setTimeout(() => setBannerSuccess(null), 8000);
      await loadStatus();
      await loadHistory();
    } catch (err) {
      setTriggerError(err.message || 'Could not start deploy');
    } finally {
      setTriggerLoading(false);
    }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (logById[id] !== undefined) return;
    setLogLoadingId(id);
    try {
      const row = await apiGet(`/admin/deploy/history/${id}`);
      setLogById((prev) => ({ ...prev, [id]: row?.deploy_log ?? '' }));
    } catch {
      setLogById((prev) => ({ ...prev, [id]: '(Could not load log)' }));
    } finally {
      setLogLoadingId(null);
    }
  };

  const historyMaxOffset = Math.max(0, history.total - HISTORY_LIMIT);
  const canPrev = historyOffset > 0;
  const canNext = historyOffset + HISTORY_LIMIT < history.total;

  const descOk = description.trim().length >= DESC_MIN;

  const canShowProdPull = latest?.status === 'success';

  const PROD_SUCCESS_PHRASE = 'DEPLOY COMPLETE — all checks passed';

  const runProdStatus = async () => {
    setProdActionLoading(true);
    setProdResultKind(null);
    setProdOutput('');
    setProdLineMessage('');
    try {
      const data = await apiGet('/admin/deploy/prod/status');
      if (data.deployStatus != null) {
        setProdDeployStatus(data.deployStatus);
      }
      const text = data.output ?? `${data.stdout ?? ''}${data.stderr ? `\n${data.stderr}` : ''}`;
      setProdOutput(text || JSON.stringify(data, null, 2));
      setProdResultKind(data.exitCode === 0 ? 'success' : 'error');
    } catch (err) {
      setProdOutput(err.message || 'Request failed');
      setProdResultKind('error');
    } finally {
      setProdActionLoading(false);
    }
  };

  const runProdDryRun = async () => {
    setProdActionLoading(true);
    setProdResultKind(null);
    setProdOutput('');
    try {
      const data = await apiPost('/admin/deploy/prod/dry-run', {});
      setProdOutput(data.output ?? '');
      setProdResultKind(data.exitCode === 0 ? 'success' : 'error');
    } catch (err) {
      setProdOutput(err.message || 'Request failed');
      setProdResultKind('error');
    } finally {
      setProdActionLoading(false);
    }
  };

  const openProdDeployConfirm = () => {
    setProdConfirmKind('deploy');
    setProdConfirmOpen(true);
  };

  const openProdRollbackConfirm = () => {
    setProdConfirmKind('rollback');
    setProdConfirmOpen(true);
  };

  const closeProdConfirm = () => {
    if (prodActionLoading) return;
    setProdConfirmOpen(false);
    setProdConfirmKind(null);
  };

  const confirmProdAction = async () => {
    if (prodConfirmKind === 'deploy') {
      setProdConfirmOpen(false);
      setProdConfirmKind(null);
      setProdActionLoading(true);
      setProdResultKind(null);
      setProdOutput('');
      setProdLineMessage('');
      setProdDeployStatus(null);
      stopProdPolling();
      try {
        const data = await apiPost('/admin/deploy/prod/deploy', {});
        if (data.status === 'deploy_started') {
          setProdLineMessage('Deploy launched on production — polling for status…');
          setProdOutput(JSON.stringify(data, null, 2));
          setProdResultKind(null);
          startProdDeployPolling();
        } else if (data.status === 'completed' && data.prodDeploySuccess === true) {
          const out = data.output ?? '';
          setProdOutput(out);
          setProdResultKind('success');
          setProdLineMessage('');
        } else {
          const out = data.output ?? JSON.stringify(data, null, 2);
          setProdOutput(out);
          const ok =
            data.prodDeploySuccess === true ||
            (typeof out === 'string' && out.includes(PROD_SUCCESS_PHRASE));
          setProdResultKind(ok ? 'success' : 'error');
          setProdLineMessage('');
        }
      } catch (err) {
        setProdOutput(err.message || 'Request failed');
        setProdResultKind('error');
        setProdLineMessage('');
      } finally {
        setProdActionLoading(false);
      }
      return;
    }
    if (prodConfirmKind === 'rollback') {
      setProdConfirmOpen(false);
      setProdConfirmKind(null);
      setProdActionLoading(true);
      setProdResultKind(null);
      setProdOutput('');
      try {
        const data = await apiPost('/admin/deploy/prod/rollback', {});
        setProdOutput(data.output ?? '');
        setProdResultKind(data.exitCode === 0 ? 'success' : 'error');
      } catch (err) {
        setProdOutput(err.message || 'Request failed');
        setProdResultKind('error');
      } finally {
        setProdActionLoading(false);
      }
    }
  };

  const prodPanelStyle = {
    marginTop: 'var(--spacing-4)',
    padding: 'var(--spacing-5)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid color-mix(in srgb, var(--color-accent-primary, #6366f1) 35%, var(--color-border-medium))',
    background:
      'linear-gradient(135deg, color-mix(in srgb, var(--color-bg-subtle, #f8fafc) 85%, var(--color-accent-primary, #6366f1) 8%) 0%, var(--color-bg-subtle, #f8fafc) 100%)',
    boxShadow: 'inset 0 1px 0 color-mix(in srgb, white 70%, transparent)',
  };

  return (
    <div>
      <AdminPageHeader
        title="Production deploy"
        subtitle="Push owner dashboard changes to GitHub and track deployment history."
      />

      {bannerSuccess && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <AdminAlert type="success" message={bannerSuccess} />
        </div>
      )}

      <AdminSection title="Deploy action">
        {shouldPoll ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
              flexWrap: 'wrap',
            }}
          >
            <AdminStatusBadge variant={statusToBadgeVariant(latest.status)}>
              {formatStatusLabel(latest.status)}
            </AdminStatusBadge>
            <p className="admin-body-text" style={{ margin: 0 }}>
              A deploy is running. This page refreshes every {POLL_MS / 1000}s. You can leave and return
              later.
            </p>
          </div>
        ) : (
          <div>
            <p className="admin-body-text" style={{ marginBottom: 'var(--spacing-4)' }}>
              Builds the owner SPA, merges to <code>deploy/prod-ready</code>, and pushes to GitHub. A
              short description is required for the audit trail.
            </p>
            <AdminButton variant="primary" onClick={openModal}>
              Deploy to production
            </AdminButton>
          </div>
        )}
      </AdminSection>

      {canShowProdPull && (
        <AdminSection title="Production pull">
          <div style={prodPanelStyle}>
            <p className="admin-body-text" style={{ marginTop: 0, marginBottom: 'var(--spacing-3)' }}>
              Pull the latest code on the <strong>production</strong> server via SSH (separate from the GitHub
              push above). Requires the deploy-trigger key in prod <code>authorized_keys</code>.
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
                marginBottom: 'var(--spacing-4)',
                alignItems: 'center',
              }}
            >
              <AdminButton
                variant="primary"
                onClick={openProdDeployConfirm}
                disabled={prodActionLoading || prodPolling}
              >
                Pull to production
              </AdminButton>
              <AdminButton variant="secondary" onClick={runProdDryRun} disabled={prodActionLoading}>
                Dry run
              </AdminButton>
              <AdminButton variant="secondary" onClick={openProdRollbackConfirm} disabled={prodActionLoading}>
                Rollback
              </AdminButton>
              <AdminButton variant="secondary" onClick={runProdStatus} disabled={prodActionLoading}>
                Prod status
              </AdminButton>
              {prodPolling && (
                <>
                  <AdminButton variant="secondary" onClick={stopProdPolling}>
                    Stop polling
                  </AdminButton>
                  <AdminButton variant="secondary" onClick={refreshProdDeployStatus}>
                    Refresh
                  </AdminButton>
                </>
              )}
            </div>
            {prodLineMessage && (
              <p className="admin-body-text" style={{ marginBottom: 'var(--spacing-2)', color: 'var(--color-text-secondary)' }}>
                {prodLineMessage}
              </p>
            )}
            {prodDeployStatus?.status && (
              <p className="admin-body-text" style={{ marginBottom: 'var(--spacing-2)', marginTop: 0 }}>
                <strong>Remote deploy state:</strong>{' '}
                <AdminStatusBadge variant={statusToBadgeVariant(prodDeployStatus.status)}>
                  {formatStatusLabel(prodDeployStatus.status)}
                </AdminStatusBadge>
              </p>
            )}
            {prodActionLoading && (
              <p className="admin-body-text" style={{ marginBottom: 'var(--spacing-3)', color: 'var(--color-text-secondary)' }}>
                Running SSH on production…
              </p>
            )}
            {prodPolling && !prodActionLoading && (
              <p className="admin-body-text" style={{ marginBottom: 'var(--spacing-3)', color: 'var(--color-text-secondary)' }}>
                Polling production deploy status every {POLL_MS / 1000}s. Use Refresh for an immediate check or Stop polling
                to pause.
              </p>
            )}
            {(prodResultKind || prodPolling) && prodOutput && (
              <div
                style={{
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${
                    prodResultKind === 'success'
                      ? 'color-mix(in srgb, var(--color-status-success-text, #166534) 40%, transparent)'
                      : prodResultKind === 'error'
                        ? 'color-mix(in srgb, var(--color-status-error-text, #b91c1c) 35%, transparent)'
                        : 'var(--color-border-medium)'
                  }`,
                  backgroundColor:
                    prodResultKind === 'success'
                      ? 'var(--color-status-success-bg, #ecfdf5)'
                      : prodResultKind === 'error'
                        ? 'var(--color-status-error-bg, #fef2f2)'
                        : 'var(--color-bg-subtle, #f8fafc)',
                  padding: 'var(--spacing-3)',
                }}
              >
                <span className="admin-label" style={{ color: 'var(--color-text-primary)' }}>
                  {prodPolling && !prodResultKind
                    ? 'Latest SSH status output'
                    : prodResultKind === 'success'
                      ? 'Output'
                      : 'Output (check failed or SSH error)'}
                </span>
                <pre
                  className="admin-body-text"
                  style={{
                    margin: 'var(--spacing-2) 0 0',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '360px',
                    overflow: 'auto',
                    fontSize: '0.8125rem',
                    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {prodOutput}
                </pre>
              </div>
            )}
          </div>
        </AdminSection>
      )}

      <AdminSection title="Current status">
        {statusLoading ? (
          <p className="admin-body-text">Loading status…</p>
        ) : noDeploys ? (
          <p className="admin-body-text">No deploys recorded yet.</p>
        ) : latest ? (
          <div className="admin-grid" style={{ gap: 'var(--spacing-4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
              <AdminStatusBadge variant={statusToBadgeVariant(latest.status)}>
                {formatStatusLabel(latest.status)}
              </AdminStatusBadge>
              <span className="admin-body-text" style={{ color: 'var(--color-text-secondary)' }}>
                {formatRelativeTime(latest.triggered_at)} ·{' '}
                {latest.triggered_at
                  ? new Date(latest.triggered_at).toLocaleString()
                  : '—'}
              </span>
            </div>
            <div>
              <span className="admin-label">Triggered by</span>
              <p className="admin-body-text" style={{ margin: 0 }}>
                {parseTriggeredByDisplay(latest.triggered_by)}
              </p>
            </div>
            <div>
              <span className="admin-label">Description</span>
              <p className="admin-body-text" style={{ margin: 0 }}>
                {latest.description || '—'}
              </p>
            </div>
            <div>
              <span className="admin-label">Git SHA</span>
              <p className="admin-body-text" style={{ margin: 0, fontFamily: 'var(--font-mono, monospace)' }}>
                {truncateSha(latest.git_sha)}
              </p>
            </div>
            {latest.status === 'failed' && latest.error_message && (
              <div
                style={{
                  padding: 'var(--spacing-3)',
                  backgroundColor: 'var(--color-status-error-bg)',
                  color: 'var(--color-status-error-text)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <span className="admin-label" style={{ color: 'inherit' }}>Error</span>
                <p className="admin-body-text" style={{ margin: 0 }}>
                  {latest.error_message}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="admin-body-text">Could not load status.</p>
        )}
      </AdminSection>

      <AdminSection title="Deploy history">
        {historyLoading ? (
          <p className="admin-body-text">Loading history…</p>
        ) : history.deploys.length === 0 ? (
          <p className="admin-body-text">No history entries.</p>
        ) : (
          <>
            <AdminTable>
              <AdminTableHeader>
                <AdminTableHeaderCell>Date / time</AdminTableHeaderCell>
                <AdminTableHeaderCell>Triggered by</AdminTableHeaderCell>
                <AdminTableHeaderCell>Description</AdminTableHeaderCell>
                <AdminTableHeaderCell>Status</AdminTableHeaderCell>
                <AdminTableHeaderCell>Git SHA</AdminTableHeaderCell>
              </AdminTableHeader>
              <tbody>
                {history.deploys.map((row) => (
                  <Fragment key={row.id}>
                    <AdminTableRow
                      onClick={() => toggleExpand(row.id)}
                      className={expandedId === row.id ? 'admin-table-row-active' : ''}
                    >
                      <AdminTableCell dataLabel="Date">
                        {row.triggered_at
                          ? new Date(row.triggered_at).toLocaleString()
                          : '—'}
                      </AdminTableCell>
                      <AdminTableCell dataLabel="By">
                        {parseTriggeredByDisplay(row.triggered_by)}
                      </AdminTableCell>
                      <AdminTableCell dataLabel="Description">
                        {row.description?.length > 80
                          ? `${row.description.slice(0, 80)}…`
                          : row.description || '—'}
                      </AdminTableCell>
                      <AdminTableCell dataLabel="Status">
                        <AdminStatusBadge variant={statusToBadgeVariant(row.status)}>
                          {formatStatusLabel(row.status)}
                        </AdminStatusBadge>
                      </AdminTableCell>
                      <AdminTableCell dataLabel="SHA">
                        <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                          {truncateSha(row.git_sha)}
                        </span>
                      </AdminTableCell>
                    </AdminTableRow>
                    {expandedId === row.id && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0, background: 'var(--color-bg-subtle, #f3f4f6)' }}>
                          <div style={{ padding: 'var(--spacing-4)' }}>
                            <span className="admin-label">Deploy log</span>
                            {logLoadingId === row.id ? (
                              <p className="admin-body-text">Loading log…</p>
                            ) : (
                              <pre
                                className="admin-body-text"
                                style={{
                                  margin: 'var(--spacing-2) 0 0',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: '320px',
                                  overflow: 'auto',
                                  padding: 'var(--spacing-3)',
                                  background: 'var(--color-bg-primary)',
                                  border: '1px solid var(--color-border-light)',
                                  borderRadius: 'var(--radius-md)',
                                  fontSize: '0.8125rem',
                                }}
                              >
                                {logById[row.id] || '—'}
                              </pre>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </AdminTable>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--spacing-2)',
                marginTop: 'var(--spacing-4)',
              }}
            >
              <AdminButton
                variant="secondary"
                disabled={!canPrev}
                onClick={() => setHistoryOffset((o) => Math.max(0, o - HISTORY_LIMIT))}
              >
                Previous
              </AdminButton>
              <AdminButton
                variant="secondary"
                disabled={!canNext}
                onClick={() =>
                  setHistoryOffset((o) => Math.min(historyMaxOffset, o + HISTORY_LIMIT))
                }
              >
                Next
              </AdminButton>
              <span className="admin-body-text" style={{ alignSelf: 'center', marginLeft: 'var(--spacing-2)' }}>
                {history.total === 0
                  ? ''
                  : `${historyOffset + 1}–${Math.min(historyOffset + HISTORY_LIMIT, history.total)} of ${history.total}`}
              </span>
            </div>
          </>
        )}
      </AdminSection>

      {prodConfirmOpen && (
        <div style={modalStyles.overlay} onClick={closeProdConfirm} role="presentation">
          <div
            style={modalStyles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="prod-confirm-title"
          >
            <div style={modalStyles.header}>
              <h2 id="prod-confirm-title" style={modalStyles.title}>
                {prodConfirmKind === 'rollback' ? 'Rollback production?' : 'Deploy to production server?'}
              </h2>
              <button
                type="button"
                style={modalStyles.closeButton}
                onClick={closeProdConfirm}
                disabled={prodActionLoading}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={modalStyles.content}>
              {prodConfirmKind === 'deploy' ? (
                <p className="admin-body-text" style={{ marginTop: 0 }}>
                  This will deploy the latest pushed code on the <strong>production</strong> server. Deploy
                  branch SHA:{' '}
                  <code style={{ wordBreak: 'break-all' }}>{latest?.git_sha || 'unknown'}</code>. Continue?
                </p>
              ) : (
                <p className="admin-body-text" style={{ marginTop: 0 }}>
                  This runs the production rollback command (restore from backup). Continue?
                </p>
              )}
            </div>
            <div style={modalStyles.footer}>
              <AdminButton variant="secondary" onClick={closeProdConfirm} disabled={prodActionLoading}>
                Cancel
              </AdminButton>
              <AdminButton variant="primary" onClick={confirmProdAction} disabled={prodActionLoading}>
                {prodActionLoading ? 'Working…' : 'Confirm'}
              </AdminButton>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div style={modalStyles.overlay} onClick={closeModal} role="presentation">
          <div
            style={modalStyles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="deploy-modal-title"
          >
            <div style={modalStyles.header}>
              <h2 id="deploy-modal-title" style={modalStyles.title}>
                Confirm production deploy
              </h2>
              <button
                type="button"
                style={modalStyles.closeButton}
                onClick={closeModal}
                disabled={triggerLoading}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={modalStyles.content}>
              <p className="admin-body-text" style={{ marginTop: 0 }}>
                Describe what you are shipping. This text is stored with the deployment record.
              </p>
              <label htmlFor="deploy-desc" style={modalStyles.label}>
                Describe the changes being deployed
              </label>
              <textarea
                id="deploy-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={modalStyles.textarea}
                disabled={triggerLoading}
                placeholder="e.g. Fix ledger export column headers; update tax review filters"
              />
              <p style={modalStyles.help}>
                Minimum {DESC_MIN} characters ({description.trim().length}/{DESC_MIN})
              </p>
              {triggerError && <div style={modalStyles.errorBox}>{triggerError}</div>}
            </div>
            <div style={modalStyles.footer}>
              <AdminButton variant="secondary" onClick={closeModal} disabled={triggerLoading}>
                Cancel
              </AdminButton>
              <AdminButton
                variant="primary"
                onClick={confirmDeploy}
                disabled={!descOk || triggerLoading}
              >
                {triggerLoading ? 'Starting…' : 'Confirm & deploy'}
              </AdminButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
